import "./Icon.css";
import cn from "classnames";
import type { MaterialSymbol } from "material-symbols";
import type { FC, HTMLAttributes } from "react";
import type { IconType } from "react-icons";
import BrandThreads from "./icons/BrandThreads.svg?react";
import BrandX from "./icons/BrandX.svg?react";
import Canva from "./icons/Canva.svg?react";
import Facebook from "./icons/Facebook.svg?react";
import Google from "./icons/Google.svg?react";
import GoogleSearchConsole from "./icons/GoogleSearchConsole.svg?react";
import Instagram from "./icons/Instagram.svg?react";
import LinkedIn from "./icons/LinkedIn.svg?react";
import Sparkle from "./icons/Sparkle.svg?react";
import TikTok from "./icons/TikTok.svg?react";
import wixLogo from "./icons/Wix.png";
import Wordpress from "./icons/Wordpress.svg?react";
import YouTube from "./icons/YouTube.svg?react";

/** Raster brand mark — matches Icon SVG sizing via width/height style. */
const Wix: SVGFC = ({ className, style }) => (
	<img
		src={wixLogo}
		alt=""
		className={className}
		draggable={false}
		style={{
			objectFit: "contain",
			display: "block",
			...style,
		}}
	/>
);

function isIconType(value: unknown): value is IconType {
	return typeof value === "function" && value.name?.startsWith("Tb");
}

type IconProps = {
	name: MaterialSymbol | SVGFC | IconType;
	variant?: "outlined" | "rounded" | "sharp";
	size?: number;
	rotate?: boolean;
	fill?: boolean;
} & HTMLAttributes<HTMLSpanElement>;

// IMPORTANT: If icons are not working ->  icons are loaded from "/packages/client/src/scss/external/material-symbols.scss" by adding the icon name to the array
// A list of all the google icons can be found here: https://fonts.google.com/icons

export const Icon: FC<IconProps> = (props) => {
	const {
		variant = "outlined",
		name: Name,
		style = {},
		size = 16,
		className,
		rotate,
		fill = false,
		...rest
	} = props;

	if (isIconType(Name)) {
		return <Name className={cn("icon", { rotate }, className)} size={size} />;
	}

	if (typeof Name !== "string") {
		return (
			<Name
				className={cn("icon", { rotate }, className)}
				style={{
					minWidth: size,
					width: size,
					minHeight: size,
					height: size,
				}}
			/>
		);
	}

	if (size) {
		style.fontSize = `${size}px`;
	}

	return (
		<span
			className={cn(
				`material-symbols-${variant}`,
				"icon",
				{ rotate },
				className,
			)}
			style={{
				...(fill ? { fontVariationSettings: "'FILL' 1" } : {}),
				...style,
			}}
			{...rest}
		>
			{Name}
		</span>
	);
};

export const IconSVG = {
	Google,
	GoogleSearchConsole,
	Facebook,
	Instagram,
	BrandX,
	BrandThreads,
	LinkedIn,
	YouTube,
	TikTok,
	Canva,
	Wordpress,
	Wix,
	Sparkle,
} as const;
